import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { decimal, plural, shortDate } from '@entities/ksp/format';
import type { KspProject } from '@entities/ksp/types';
import { useGetKspProjectsQuery, useGetKspSummaryQuery } from '@shared/api/endpoints/ksp';
import { Chip, Muted, Notice, Small, Stack, TileValue } from '@widgets/ksp-workspace/parts';
import { finishStatus } from './overview/pulse';

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
`;
const CardLink = styled(Link)<{ $muted?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  min-height: 170px;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme, $muted }) =>
    $muted ? theme.colors.background : theme.colors.backgroundElevated};
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s ease;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;
const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const Bottom = styled.div`
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;
const Action = styled.span`
  margin-left: auto;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary};
`;

/** Карточка проекта с КСП: успеваем ли к договорному сроку, по опубликованному расчёту. */
function ActiveCard({ project }: { project: KspProject }) {
  const { data, isLoading, error } = useGetKspSummaryQuery(project.id);
  const snapshot = data?.snapshot ?? null;
  const timezone = snapshot?.timezone ?? project.timezone ?? 'Europe/Moscow';
  const finish = finishStatus(snapshot);
  const alerts = data?.operational?.open_milestone_alert_count ?? 0;
  const physical = snapshot?.kpis.find((kpi) => kpi.code.replace(/^ksp\./, '') === 'physical_progress');
  return (
    <CardLink to={`/agents/ksp/projects/${project.id}`}>
      <Title title={project.name}>{project.name}</Title>
      {isLoading ? (
        <Muted>Загружается сводка…</Muted>
      ) : error ? (
        <Muted>Сводка недоступна — откройте проект.</Muted>
      ) : snapshot ? (
        <>
          <TileValue $tone={finish.tone}>{finish.value}</TileValue>
          <Small>
            Прогноз {shortDate(snapshot.forecast_finish, timezone)} · договор{' '}
            {shortDate(snapshot.contract_finish, timezone)}
          </Small>
          <Small>
            Выполнено{' '}
            {physical?.fact_pct != null ? decimal(physical.fact_pct, ' %') : '— (веса работ не утверждены)'}
          </Small>
        </>
      ) : (
        <Muted>Расчёт ещё не опубликован.</Muted>
      )}
      <Bottom>
        {snapshot && <Small>Расчёт {shortDate(snapshot.calculated_at, timezone)}</Small>}
        {alerts > 0 && (
          <Chip $tone="bad">
            {alerts} {plural(alerts, 'предупреждение', 'предупреждения', 'предупреждений')} по вехам
          </Chip>
        )}
        <Action>Открыть →</Action>
      </Bottom>
    </CardLink>
  );
}

/** Проект ПТО без КСП: начать планирование. */
function IdleCard({ project }: { project: KspProject }) {
  return (
    <CardLink to={`/agents/ksp/projects/${project.id}`} $muted>
      <Title title={project.name}>{project.name}</Title>
      <Muted>КСП по проекту не начат.</Muted>
      <Bottom>
        <Chip>нет графика</Chip>
        <Action>Начать планирование →</Action>
      </Bottom>
    </CardLink>
  );
}

export function ProjectsView() {
  const { data, error, isLoading } = useGetKspProjectsQuery();
  const items = data?.items ?? [];
  const active = items.filter((project) => project.ksp_version != null);
  const idle = items.filter((project) => project.ksp_version == null);
  return (
    <Stack>
      <h1>ОСНОВА.КСП</h1>
      <Muted>
        График строительства: договорные сроки, прогноз, факт и вехи проектов.
        {items.length > 0 &&
          ` КСП ведётся по ${active.length} ${plural(active.length, 'проекту', 'проектам', 'проектам')} из ${items.length}.`}
      </Muted>
      {isLoading && <Notice>Загружаются доступные проекты…</Notice>}
      {error && (
        <Notice $error>Проекты недоступны. Повторите загрузку после восстановления связи.</Notice>
      )}
      {active.length > 0 && (
        <Cards>
          {active.map((project) => (
            <ActiveCard key={project.id} project={project} />
          ))}
        </Cards>
      )}
      {idle.length > 0 && (
        <>
          <h3>Без КСП</h3>
          <Cards>
            {idle.map((project) => (
              <IdleCard key={project.id} project={project} />
            ))}
          </Cards>
        </>
      )}
      {data && items.length === 0 && (
        <Notice>
          Нет доступных проектов. КСП ведётся по проектам ОСНОВА.Инженер (ПТО): попросите добавить
          вас в участники проекта.
        </Notice>
      )}
    </Stack>
  );
}
